import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { useAuthStore } from "@/store/auth-store";
import type { PaginatedResponse } from "@/types/api";

export interface Location {
  id: string;
  name: string;
  timezone: string;
}

const LOCATION_FIELDS = ["id", "name", "timezone"] as const satisfies readonly (keyof Location)[];

export function useLocations() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const organizationId = useAuthStore((s) => s.organizationId);

  return useQuery({
    queryKey: ["locations", organizationId],
    queryFn: async () => {
      const params = new URLSearchParams({ fields: LOCATION_FIELDS.join(",") });
      const res = await api.get(`locations/?${params}`).json<PaginatedResponse<Location>>();
      return res.items;
    },
    enabled: isAuthenticated && !!organizationId,
  });
}

export function useCreateLocation() {
  const queryClient = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);

  return useMutation({
    mutationFn: (data: { name: string }) => api.post("locations/", { json: data }).json<Location>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["locations", organizationId] });
    },
  });
}

export function useUpdateLocation() {
  const queryClient = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);

  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; name: string }) =>
      api.patch(`locations/${id}/`, { json: data }).json<Location>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["locations", organizationId] });
    },
  });
}

export function useDeleteLocation() {
  const queryClient = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);

  return useMutation({
    mutationFn: (id: string) => api.delete(`locations/${id}/`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["locations", organizationId] });
    },
  });
}

export function useActiveLocation(): Location | undefined {
  const locationId = useAuthStore((s) => s.locationId);
  const { data } = useLocations();
  return locationId ? data?.find((l) => l.id === locationId) : undefined;
}

export function useActiveLocationTimezone(): string | undefined {
  return useActiveLocation()?.timezone || undefined;
}

export interface LocationSettings {
  default_reservation_duration_minutes: number;
  no_show_tolerance_minutes: number;
  auto_complete_grace_minutes: number;
  proximity_window_minutes: number;
  turnover_buffer_minutes: number;
  minimum_advance_minutes: number;
  night_shift_split_time: string; // "HH:MM:SS"
}

export function useLocationSettings() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const locationId = useAuthStore((s) => s.locationId);

  return useQuery({
    queryKey: ["location-settings", locationId],
    queryFn: () => api.get("location-settings/").json<LocationSettings>(),
    enabled: isAuthenticated && !!locationId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateLocationSettings() {
  const queryClient = useQueryClient();
  const locationId = useAuthStore((s) => s.locationId);

  return useMutation({
    mutationFn: (data: Partial<LocationSettings>) =>
      api.patch("location-settings/", { json: data }).json<LocationSettings>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["location-settings", locationId] });
    },
  });
}
